import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useHotloadStatus } from '@ctw/shared/hooks/use-hotload-status';
import { isHotEligibleResourceType } from '@ctw/shared/utils/fhir/resource-mappings';
import { useEffect, useState } from 'react';

export interface UseHotloadReadyOptions {
  upid?: string;
  /**
   * The FHIR resource types to wait on. Only hot-eligible types (see `isHotEligibleResourceType`)
   * can actually gate: readiness is reached once every *hot-eligible* type among these is present in
   * the patient's `loadedResourceTypes` — without waiting for the rest of the record to finish
   * hotloading. If none of the requested types are hot-eligible (e.g. `Encounter`), hotstorage will
   * never report them, so there's nothing to wait on and the hook is ready immediately rather than
   * blocking forever. When omitted (or empty), readiness waits for the patient's hotload to settle
   * (`hotloaded`, `partially_hotloaded`, or `hotloaded_with_omissions`).
   */
  resourceTypes?: Array<string>;
}

/**
 * Reports whether hotstorage-backed content for a patient is ready to render (and, for callers that
 * defer data fetching, ready to query). Ready when blocking is disabled (`ctw-hotload-blocking`
 * off), there is no UPID, the poll timed out or hard-errored, the requested `resourceTypes` are all
 * hotloaded, or the patient's hotload has settled (`hotloaded`, `partially_hotloaded`, or
 * `hotloaded_with_omissions` — see `useHotloadStatus`). A settled-but-incomplete record still opens
 * the gate: the patient's next trigger POST is the only thing that could recover a missing or
 * omitted type, and that happens on a future visit, not from more polling now.
 *
 * Latches open per-patient: once ready for a UPID, it stays ready even if a later poll regresses (a
 * regressed poll, an errored refetch, a feature-flag re-sync) — reverting would unmount/remount
 * gated content and flicker the UI. Readiness is only re-evaluated when the patient (UPID) changes.
 *
 * Backs both `<HotloadGate>` and per-card gating, so they share one definition of "ready".
 */
export const useHotloadReady = ({ upid, resourceTypes }: UseHotloadReadyOptions): boolean => {
  const hasHotloadBlocking = useFeatureFlag('ctw-hotload-blocking');
  const { isSettled, loadedResourceTypes, hasTimedOut, isError } = useHotloadStatus(upid);

  const shouldPassThrough = !(hasHotloadBlocking && upid) || hasTimedOut || isError;

  const requestedResourceTypes = resourceTypes ?? [];
  const hotEligibleRequestedResourceTypes =
    requestedResourceTypes.filter(isHotEligibleResourceType);

  // The gate was asked to wait on at least one resource type, but none of them are ever
  // hotstorage-backed (e.g. Encounter, EpisodeOfCare) — hotstorage will never report them as loaded,
  // so there's nothing to actually wait on. Pass through rather than blocking on data that can never
  // arrive this way.
  const requestedOnlyNonHotEligibleTypes =
    requestedResourceTypes.length > 0 && hotEligibleRequestedResourceTypes.length === 0;

  // When hot-eligible resource types are requested, become ready the moment they're all hotloaded —
  // a consumer need not wait for the entire record. A settled patient always satisfies the check
  // too (covering both the edge where a requested type has zero records and so never lands in the
  // per-type list, and the case where the requested type itself never loaded — once settled, no
  // further polling will ever produce it this session); without requested types, settling is the
  // only readiness signal.
  const hasRequestedResourceTypes = hotEligibleRequestedResourceTypes.length > 0;
  const requestedResourceTypesLoaded =
    hasRequestedResourceTypes &&
    hotEligibleRequestedResourceTypes.every((resourceType) =>
      loadedResourceTypes.includes(resourceType),
    );

  const ready =
    shouldPassThrough ||
    isSettled ||
    requestedResourceTypesLoaded ||
    requestedOnlyNonHotEligibleTypes;

  const [readyUpid, setReadyUpid] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (ready && upid) {
      setReadyUpid(upid);
    }
  }, [ready, upid]);

  return ready || readyUpid === upid;
};
